import _ from 'lodash';
import { metricKeys } from '@pulse/metrics';
import {
  DEFAULT_SENSITIVITY_CONFIG_FOR_AD_METRIC,
  DEFAULT_SENSITIVITY_CONFIG_FOR_APP_METRIC,
  DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC,
} from '@src/utils/aiAlerts/mapping';
import { isAppModule, PRODUCT_MODE_MAP } from '@src/utils/constants';
import { AI_ALERT_STANDARD_METRICS_MAP, ECO_AI_ALERT_NWR_METRICS } from '../../constants';
import { getMetricUnitByKey, getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';

export const getDefaultMetricsSensitivityConfig = (sensitivityData, aiAlertMetricsKeys) => {
  const sensitivityDataForForm = [...sensitivityData];
  aiAlertMetricsKeys.forEach((metricKey) => {
    const sensitivityConfig = sensitivityData.find(
      (sensitivity) => sensitivity.metricKey === metricKey,
    );
    // default is not required for custom metrics here as it already has value in sensitivity.
    // EI standard metrics are now selectable: if a metric is missing from the saved config
    // it was disabled, so do NOT re-inject a default (that would re-check the checkbox).
    if (!sensitivityConfig) {
      if (AI_ALERT_STANDARD_METRICS_MAP.AI.includes(metricKey)) {
        sensitivityDataForForm.push({ ...DEFAULT_SENSITIVITY_CONFIG_FOR_AD_METRIC, metricKey });
      }
      if (AI_ALERT_STANDARD_METRICS_MAP.APP.includes(metricKey)) {
        sensitivityDataForForm.push({ ...DEFAULT_SENSITIVITY_CONFIG_FOR_APP_METRIC, metricKey });
      }
    }
  });
  return sensitivityDataForForm;
};

export const prepareDefaultSensitivityConfig = (metricKey, type) => {
  switch (type) {
    case PRODUCT_MODE_MAP.EI:
      return {
        ...DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC,
        metricKey,
        // Fusion CCS write keys PercentageDrop off `metric === AUTOALERTPCP`.
        ...(metricKey === metricKeys.ConcurrentPlays ? { metric: 'AUTOALERTPCP' } : {}),
      };
    case PRODUCT_MODE_MAP.APP:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_APP_METRIC, metricKey };
    case PRODUCT_MODE_MAP.AI:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_AD_METRIC, metricKey };
    default:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC, metricKey };
  }
};

export const findDifferencesWithResult = (currentResults, previousResults) => {
  const changesInCommonResults = [];
  const removedSensitivities = [];
  const newlyAddedSensitivity = [];

  // Find changed and extra objects
  previousResults.forEach((previousObj) => {
    const currentObj = _.find(currentResults, { metricKey: previousObj.metricKey });
    if (currentObj) {
      if (!_.isEqual(currentObj, previousObj)) {
        changesInCommonResults.push(currentObj);
      }
    } else {
      removedSensitivities.push(previousObj);
    }
  });

  // Find extra objects in originalArray
  currentResults.forEach((currentObj) => {
    const compareObj = _.find(previousResults, { metricKey: currentObj.metricKey });
    if (!compareObj) {
      newlyAddedSensitivity.push(currentObj);
    }
  });

  const removedSensitivitiesUpdated = removedSensitivities.map((sensitivity) => {
    return { ...sensitivity, isDeleted: true };
  });

  const buildResponse = [
    ...changesInCommonResults,
    ...newlyAddedSensitivity,
    ...removedSensitivitiesUpdated,
  ];

  return buildResponse;
};

export const isSearchedItem = (itemName: string, searchText: string) => {
  const regex = new RegExp(searchText, 'i'); // ignore case
  return regex.test(itemName);
};

export const hasImpactedCount = (metricKey, ecoAIAlert2 = false) => {
  const percentageMetric = getPercentageMetricByKey({
    metricKey,
    defaultValue: metricKey,
  });

  return (
    isAppModule &&
    ecoAIAlert2 &&
    !ECO_AI_ALERT_NWR_METRICS.includes(metricKey) &&
    getMetricUnitByKey({ metricKey: percentageMetric }) === '%'
  );
};
